import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import RangoFechas, { rangoInicial } from '../../components/RangoFechas';
import { money, moneyK, fecha, mesLabel, Title, Badge, Empty, Kpi, ESTADO_RENOVACION, ESTADO_COBRO } from './ui';
import AgenciaFinanzas from './Finanzas';
import { ESTADO_PROYECTO } from './Proyecto';

const SOLAPAS = [
  { key: 'finanzas', label: 'Finanzas' },
  { key: 'cobros', label: 'Cobros' },
  { key: 'clientes', label: 'Clientes' },
  { key: 'proyectos', label: 'Proyectos' },
  { key: 'servicios', label: 'Servicios' },
  { key: 'hosting', label: 'Hosting' },
  { key: 'propuestas', label: 'Propuestas' },
  { key: 'actividad', label: 'Actividad y mails' },
];
const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };
const tick = { fill: 'var(--chart-text)', fontSize: 11 };
const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };

export default function AgenciaReportes() {
  const [solapa, setSolapa] = useState('finanzas');
  const [rango, setRango] = useState(() => rangoInicial('doce'));
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (['finanzas', 'actividad'].includes(solapa)) return;
    setD(null);
    api.get('/agency/panorama', { params: { desde: rango.desde, hasta: rango.hasta } })
      .then(r => setD(r.data))
      .catch(e => toast.error(e.response?.data?.error || 'No se pudo cargar el reporte'));
  }, [solapa, rango]);

  const chip = (a) => ({
    padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: '1px solid ' + (a ? 'var(--accent)' : 'var(--border)'),
    background: a ? 'var(--accent)' : 'transparent', color: a ? '#fff' : 'var(--text-secondary)',
  });

  return (
    <div>
      <Title>Reportes</Title>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {SOLAPAS.map(s => <div key={s.key} style={chip(solapa === s.key)} onClick={() => setSolapa(s.key)}>{s.label}</div>)}
      </div>

      {solapa !== 'actividad' && <RangoFechas valor={rango} onChange={setRango} />}

      {solapa === 'actividad' ? <Actividad />
        : solapa === 'finanzas' ? <AgenciaFinanzas embebido rango={rango} />
        : !d ? <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>
          : solapa === 'cobros' ? <Cobros d={d} navigate={navigate} rango={rango} />
            : solapa === 'clientes' ? <Clientes d={d} navigate={navigate} />
              : solapa === 'proyectos' ? <Proyectos d={d} navigate={navigate} />
                : solapa === 'servicios' ? <Servicios d={d} navigate={navigate} />
                  : solapa === 'hosting' ? <Hosting d={d} navigate={navigate} />
                    : <Propuestas d={d} navigate={navigate} />}
    </div>
  );
}

const Tabla = ({ children }) => <table style={{ width: '100%', borderCollapse: 'collapse' }}>{children}</table>;
const Card = ({ titulo, sub, children }) => (
  <div className="card" style={{ marginBottom: 18 }}>
    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: sub ? 2 : 12 }}>{titulo}</h3>
    {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{sub}</div>}
    {children}
  </div>
);

function Cobros({ d, navigate, rango }) {
  const est = d.cobros.por_estado;
  const suma = (f) => est.filter(f).reduce((a, r) => a + Number(r.total || 0), 0);
  const cuenta = (f) => est.filter(f).reduce((a, r) => a + Number(r.cant || 0), 0);
  const demora = d.cobros.demora[0] && d.cobros.demora[0].dias;
  const [detalle, setDetalle] = useState(null);   // qué listado estamos mirando
  const [filas, setFilas] = useState(null);

  const abrir = (clave, estado) => {
    if (detalle === clave) { setDetalle(null); return; }
    setDetalle(clave); setFilas(null);
    api.get('/agency/cobros', { params: { estado, desde: rango.desde, hasta: rango.hasta, limit: 300 } })
      .then(r => setFilas(r.data.cobros)).catch(() => toast.error('No se pudo cargar el detalle'));
  };

  const TITULOS = {
    pagado: 'Cobros pagados', abiertos: 'Pendientes de cobro',
    informado: 'Pagos informados por el cliente', a_pasar: 'Cuotas a pasar',
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Cobrado" value={moneyK(suma(r => r.estado === 'pagado'))} color="#22c55e"
          sub={`${cuenta(r => r.estado === 'pagado')} cobros · ver detalle`} onClick={() => abrir('pagado', 'pagado')} />
        <Kpi label="Pendiente" value={moneyK(suma(r => ['pendiente', 'vencido', 'informado'].includes(r.estado) && r.habilitado))} color="#f59e0b"
          sub={`${cuenta(r => ['pendiente', 'vencido', 'informado'].includes(r.estado) && r.habilitado)} cobros · ver detalle`}
          onClick={() => abrir('abiertos', 'abiertos')} />
        <Kpi label="Informados" value={moneyK(suma(r => r.estado === 'informado'))} color="#0ea5e9"
          sub={`${cuenta(r => r.estado === 'informado')} avisaron que pagaron`} onClick={() => abrir('informado', 'informado')} />
        <Kpi label="A pasar" value={moneyK(suma(r => !r.habilitado && r.estado !== 'anulado'))} color="#64748b"
          sub={`${cuenta(r => !r.habilitado && r.estado !== 'anulado')} cuotas futuras`} onClick={() => abrir('a_pasar', 'a_pasar')} />
        <Kpi label="Demora promedio" value={demora === null || demora === undefined ? '—' : `${demora} días`} sub="respecto del vencimiento" />
      </div>

      {detalle && (
        <Card titulo={TITULOS[detalle]} sub="Del período elegido. Tocá una fila para abrir el cliente.">
          {!filas ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cargando...</div>
            : filas.length ? (
              <Tabla>
                <thead><tr><th style={th}>Cliente</th><th style={th}>Concepto</th><th style={th}>Vence</th>
                  <th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th></tr></thead>
                <tbody>{filas.map(c => {
                  const e = ESTADO_COBRO[c.estado] || {};
                  return (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/agencia/clientes/' + c.cliente_id)}>
                      <td style={{ ...td, fontWeight: 600 }}>{c.cliente}</td>
                      <td style={td}>{c.concepto || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>{fecha(c.vencimiento)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                      <td style={td}><Badge label={c.habilitado ? e.label : 'A pasar'} color={c.habilitado ? e.color : '#64748b'} bg={c.habilitado ? e.bg : 'rgba(100,116,139,0.15)'} /></td>
                    </tr>
                  );
                })}</tbody>
              </Tabla>
            ) : <Empty>No hay cobros en ese estado dentro del período</Empty>}
        </Card>
      )}
      <Card titulo="Clientes con deuda vencida" sub="Cobros habilitados que pasaron su vencimiento">
        {d.cobros.morosidad.length ? (
          <Tabla>
            <thead><tr><th style={th}>Cliente</th><th style={th}>Desde</th><th style={th}>Cobros</th><th style={{ ...th, textAlign: 'right' }}>Total</th></tr></thead>
            <tbody>{d.cobros.morosidad.map(c => (
              <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/agencia/clientes/' + c.id)}>
                <td style={{ ...td, fontWeight: 600 }}>{c.nombre}</td>
                <td style={td}>{fecha(c.desde)}</td>
                <td style={td}>{c.cant}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>{money(c.total)}</td>
              </tr>
            ))}</tbody>
          </Tabla>
        ) : <Empty>No hay deuda vencida 🎉</Empty>}
      </Card>
    </div>
  );
}

function Clientes({ d, navigate }) {
  const activos = d.clientes.resumen.filter(r => r.estado === 'activo').reduce((a, r) => a + r.cant, 0);
  const agencias = d.clientes.resumen.filter(r => r.tipo === 'agencia').reduce((a, r) => a + r.cant, 0);
  const nuevos = d.clientes.nuevos.map(n => ({ mes: mesLabel(n.mes), cant: Number(n.cant) }));
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Clientes activos" value={activos} />
        <Kpi label="Agencias" value={agencias} />
        <Kpi label="Sin facturar hace 6+ meses" value={d.clientes.sin_movimiento.length} color="#f59e0b" />
      </div>
      <Card titulo="Los que más facturaron" sub="En el período seleccionado">
        <Tabla>
          <tbody>{d.clientes.top.map(c => (
            <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/agencia/clientes/' + c.id)}>
              <td style={{ ...td, fontWeight: 600 }}>{c.nombre}</td>
              <td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{c.cobros} cobros</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(c.total)}</td>
            </tr>
          ))}</tbody>
        </Tabla>
      </Card>
      {nuevos.length > 0 && (
        <Card titulo="Clientes nuevos por mes">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={nuevos} margin={{ left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="mes" tick={tick} /><YAxis tick={tick} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="cant" name="Altas" fill="var(--accent)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
      <Card titulo="Clientes dormidos" sub="Activos pero sin cobros en los últimos 6 meses: buenos candidatos para reactivar">
        {d.clientes.sin_movimiento.length ? (
          <Tabla>
            <tbody>{d.clientes.sin_movimiento.map(c => (
              <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/agencia/clientes/' + c.id)}>
                <td style={{ ...td, fontWeight: 600 }}>{c.nombre}</td>
                <td style={{ ...td, textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>
                  {c.ultimo ? `último cobro ${fecha(c.ultimo)}` : 'sin cobros'}</td>
              </tr>
            ))}</tbody>
          </Tabla>
        ) : <Empty>Ninguno</Empty>}
      </Card>
    </div>
  );
}

function Proyectos({ d, navigate }) {
  const total = d.proyectos.por_estado.reduce((a, r) => a + r.cant, 0);
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Proyectos" value={total} />
        {d.proyectos.por_estado.filter(r => !['terminado', 'cancelado'].includes(r.estado)).map(r => (
          <Kpi key={r.estado} label={(ESTADO_PROYECTO[r.estado] || {}).label || r.estado} value={r.cant}
            sub={r.total ? moneyK(r.total) : null} color={(ESTADO_PROYECTO[r.estado] || {}).color} />
        ))}
      </div>
      <Card titulo="Proyectos en curso" sub="Ordenados por el más reciente. El último movimiento te dice cuáles están quietos.">
        {d.proyectos.activos.length ? (
          <Tabla>
            <thead><tr><th style={th}>Proyecto</th><th style={th}>Cliente</th><th style={th}>Estado</th>
              <th style={th}>Último movimiento</th><th style={{ ...th, textAlign: 'right' }}>Por cobrar</th></tr></thead>
            <tbody>{d.proyectos.activos.map(p => {
              const e = ESTADO_PROYECTO[p.estado] || {};
              const dias = p.ultimo_movimiento ? Math.round((Date.now() - new Date(p.ultimo_movimiento)) / 86400000) : null;
              return (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/agencia/proyectos/' + p.id)}>
                  <td style={{ ...td, fontWeight: 600 }}>{p.nombre}</td>
                  <td style={td}>{p.cliente}</td>
                  <td style={td}><Badge label={e.label} color={e.color} bg={e.bg} /></td>
                  <td style={{ ...td, fontSize: 12, color: dias > 30 ? '#f59e0b' : 'var(--text-muted)' }}>
                    {dias === null ? 'sin movimientos' : dias === 0 ? 'hoy' : `hace ${dias} días`}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{p.por_cobrar ? money(p.por_cobrar) : '—'}</td>
                </tr>
              );
            })}</tbody>
          </Tabla>
        ) : <Empty>No hay proyectos en curso</Empty>}
      </Card>
    </div>
  );
}

function Servicios({ d, navigate }) {
  const mensual = d.servicios.abonos.reduce((a, r) => a + Number(r.monto || 0), 0);
  const sinAjuste = d.servicios.abonos.filter(a => a.dias_sin_ajuste === null || a.dias_sin_ajuste >= 90);
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Facturación mensual" value={moneyK(mensual)} sub={`${d.servicios.abonos.length} abonos`} color="var(--accent)" />
        <Kpi label="Promedio por abono" value={moneyK(d.servicios.abonos.length ? mensual / d.servicios.abonos.length : 0)} />
        <Kpi label="Sin ajustar hace 3+ meses" value={sinAjuste.length} color={sinAjuste.length ? '#f59e0b' : undefined} />
      </div>
      <Card titulo="Qué servicios tenemos activos" sub="Abonos y renovaciones agrupados">
        <Tabla>
          <tbody>{d.servicios.por_servicio.map(s => (
            <tr key={s.servicio}>
              <td style={{ ...td, fontWeight: 600 }}>{s.servicio}</td>
              <td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{s.cant} clientes</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(s.total)}</td>
            </tr>
          ))}</tbody>
        </Tabla>
      </Card>
      <Card titulo="Abonos activos">
        <Tabla>
          <thead><tr><th style={th}>Cliente</th><th style={th}>Servicio</th><th style={th}>Último ajuste</th><th style={{ ...th, textAlign: 'right' }}>Mensual</th></tr></thead>
          <tbody>{d.servicios.abonos.map(a => (
            <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/agencia/servicios/' + a.id)}>
              <td style={{ ...td, fontWeight: 600 }}>{a.cliente}</td>
              <td style={td}>{a.nombre}</td>
              <td style={{ ...td, fontSize: 12, color: a.dias_sin_ajuste >= 180 ? '#ef4444' : a.dias_sin_ajuste >= 90 ? '#f59e0b' : 'var(--text-muted)' }}>
                {a.dias_sin_ajuste === null ? 'sin registro' : `hace ${Math.round(a.dias_sin_ajuste / 30)} meses`}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(a.monto)}</td>
            </tr>
          ))}</tbody>
        </Tabla>
      </Card>
    </div>
  );
}

function Hosting({ d, navigate }) {
  const t = d.hosting.total[0] || {};
  const serie = d.hosting.por_mes.map(m => ({ mes: mesLabel(m.mes), cant: Number(m.cant), total: Number(m.total) }));
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Hostings activos" value={t.cant || 0} />
        <Kpi label="Facturación anual" value={moneyK(t.total)} color="var(--accent)" />
        <Kpi label="Promedio por hosting" value={moneyK(t.cant ? t.total / t.cant : 0)} />
      </div>
      <Card titulo="Vencimientos por mes" sub="Cada uno vence el último día del mes">
        {serie.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={serie} margin={{ left: -6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="mes" tick={tick} /><YAxis tick={tick} tickFormatter={moneyK} width={70} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [n === 'total' ? money(v) : v, n === 'total' ? 'Monto' : 'Cantidad']} />
              <Bar dataKey="total" name="total" fill="var(--accent)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty>Sin datos</Empty>}
      </Card>
      <Card titulo="Estado de las renovaciones">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {d.hosting.por_estado.map(r => {
            const e = ESTADO_RENOVACION[r.estado] || {};
            return (
              <div key={r.estado} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', minWidth: 130, cursor: 'pointer' }}
                onClick={() => navigate('/agencia/renovaciones')}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{e.label || 'Sin estado'}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: e.color || 'var(--text-primary)' }}>{r.cant}</div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function Propuestas({ d, navigate }) {
  const ESTADO = { borrador: 'Borrador', a_preparar: 'A preparar', a_ofrecer: 'A ofrecer', enviada: 'Enviada',
    vista: 'En seguimiento', confirmada: 'Cerrada', no_cerro: 'No cerró' };
  const cerradas = d.propuestas.filter(p => ['confirmada', 'no_cerro'].includes(p.estado)).reduce((a, r) => a + r.cant, 0);
  const ganadas = (d.propuestas.find(p => p.estado === 'confirmada') || {}).cant || 0;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Efectividad" value={cerradas ? Math.round((ganadas / cerradas) * 100) + '%' : '—'} sub={`${cerradas} decididas`} color="#22c55e" />
        {d.propuestas.map(p => (
          <Kpi key={p.estado} label={ESTADO[p.estado] || p.estado} value={p.cant} sub={p.total ? moneyK(p.total) : null} />
        ))}
      </div>
      <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate('/agencia/propuestas')}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Ver todas las propuestas y su seguimiento →</div>
      </div>
    </div>
  );
}

/* Qué hizo la plataforma y qué mails salieron */
function Actividad() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/agency/registro').then(x => setD(x.data)).catch(() => toast.error('No se pudo cargar')); }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;
  const cuando = (f) => {
    const t = new Date(f);
    return t.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }) + ' ' + t.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18 }}>
      <Card titulo="Mails enviados" sub="Todo lo que salió desde la plataforma, con su resultado">
        {d.emails.length ? d.emails.map(e => (
          <div key={e.id} style={{ borderTop: '1px solid var(--border)', padding: '9px 0', fontSize: 13 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{cuando(e.created_at)}</span>
              <span style={{ fontWeight: 600 }}>{e.asunto}</span>
              {e.estado === 'error' && <Badge label="Error" color="#ef4444" bg="rgba(239,68,68,0.15)" />}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{e.destinatarios}</div>
            {e.estado === 'error' && <div style={{ fontSize: 11, color: '#ef4444' }}>{e.detalle}</div>}
          </div>
        )) : <Empty>Todavía no se envió ningún mail</Empty>}
      </Card>
      <Card titulo="Movimientos de la plataforma" sub="Cobros generados, pagos, propuestas, ajustes de precio">
        {d.actividad.length ? d.actividad.map(a => (
          <div key={a.id} style={{ borderTop: '1px solid var(--border)', padding: '9px 0', fontSize: 13 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{cuando(a.created_at)}</span>
              <span style={{ fontWeight: 600 }}>{a.accion.replace(/_/g, ' ')}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{a.detalle}</div>
          </div>
        )) : <Empty>Sin movimientos</Empty>}
      </Card>
    </div>
  );
}
